import { useState, useEffect, useCallback } from 'react';
import { Table, Badge, Button, Modal, Input, Select, Pagination } from '../../components/ui';
import { apiFetch } from '../../services/api';

const STATUS_COLORS = {
  paid:        'var(--green-light)',
  pending:     'var(--yellow)',
  processing:  'var(--blue-light)',
  failed:      'var(--red-light)',
  refunded:    'var(--purple-light)',
  waived:      'var(--text-muted)',
  draft:       'var(--text-muted)',
  sent:        'var(--blue-light)',
  overdue:     'var(--red-light)',
  cancelled:   'var(--text-muted)',
};

const METHOD_LABELS = {
  mtn_momo:      'MTN MoMo',
  stripe:        'Stripe',
  bank_transfer: 'Bank Transfer',
  cash:          'Cash',
  scholarship:   'Scholarship',
  waived:        'Waived',
};

const TYPE_LABELS = {
  program_fee:    'Program Fee',
  service_fee:    'Service Fee',
  internship_fee: 'Internship Fee',
  donation:       'Community Support',
  subscription:   'Subscription',
  refund:         'Refund',
  other:          'Other',
};

function fmt(n) { return `RWF ${Number(n || 0).toLocaleString()}`; }

export default function PaymentsPage() {
  const [tab, setTab]               = useState('overview');
  const [summary, setSummary]       = useState(null);
  const [invSummary, setInvSummary] = useState(null);
  const [transactions, setTxns]     = useState([]);
  const [invoices, setInvoices]     = useState([]);
  const [pagination, setPag]        = useState(null);
  const [loading, setLoading]       = useState(true);
  const [page, setPage]             = useState(1);
  const [filter, setFilter]         = useState({ status: '', type: '' });
  const [modal, setModal]           = useState(null);
  const [selected, setSel]          = useState(null);
  const [saving, setSaving]         = useState(false);
  const [invForm, setInvForm]       = useState({});

  const token = localStorage.getItem('mh_token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const loadSummary = useCallback(async () => {
    try {
      const [txRes, invRes] = await Promise.all([
        apiFetch('/admin/transactions/summary', { headers }),
        apiFetch('/admin/invoices/summary', { headers }),
      ]);
      const txData  = await txRes.json();
      const invData = await invRes.json();
      setSummary(txData.data); setInvSummary(invData.data);
    } catch {}
  }, []);

  const loadTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filter.status) params.set('status', filter.status);
      if (filter.type)   params.set('type', filter.type);
      const res = await apiFetch(`/admin/transactions?${params}`, { headers });
      const data = await res.json();
      setTxns(data.data || []); setPag(data.pagination);
    } catch { setTxns([]); } finally { setLoading(false); }
  }, [page, filter]);

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filter.status) params.set('status', filter.status);
      const res = await apiFetch(`/admin/invoices?${params}`, { headers });
      const data = await res.json();
      setInvoices(data.data || []); setPag(data.pagination);
    } catch { setInvoices([]); } finally { setLoading(false); }
  }, [page, filter]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => {
    if (tab === 'transactions') loadTransactions();
    else if (tab === 'invoices') loadInvoices();
  }, [tab, loadTransactions, loadInvoices]);
  useEffect(() => { setPage(1); setFilter({ status: '', type: '' }); }, [tab]);

  const openCreateInvoice = () => {
    setInvForm({
      client_name: '', client_email: '', client_phone: '',
      type: 'service_fee', description: '',
      line_items: [{ name: '', qty: 1, unit_price: 0, total: 0 }],
      discount_pct: 0, tax_pct: 18, currency: 'RWF',
      due_date: '', notes: '',
    });
    setSel(null); setModal('invoice');
  };

  const openEditInvoice = (inv) => {
    setInvForm({
      client_name: inv.client_name, client_email: inv.client_email,
      client_phone: inv.client_phone || '', type: inv.type,
      description: inv.description, line_items: inv.line_items || [],
      discount_pct: inv.discount_pct || 0, tax_pct: inv.tax_pct || 0,
      currency: inv.currency || 'RWF', status: inv.status,
      due_date: inv.due_date?.slice(0,10) || '', notes: inv.notes || '',
    });
    setSel(inv); setModal('invoice');
  };

  const calcTotals = (items, discPct, taxPct) => {
    const subtotal = items.reduce((s, i) => s + (i.total || 0), 0);
    const discount = subtotal * (discPct || 0) / 100;
    const tax      = (subtotal - discount) * (taxPct || 0) / 100;
    return { subtotal, discount, tax, total: subtotal - discount + tax };
  };

  const updateLineItem = (idx, field, val) => {
    setInvForm(f => {
      const items = [...f.line_items];
      items[idx] = { ...items[idx], [field]: val };
      if (field === 'qty' || field === 'unit_price') {
        items[idx].total = (items[idx].qty || 0) * (items[idx].unit_price || 0);
      }
      return { ...f, line_items: items };
    });
  };

  const handleSaveInvoice = async () => {
    setSaving(true);
    try {
      const totals = calcTotals(invForm.line_items, invForm.discount_pct, invForm.tax_pct);
      const payload = { ...invForm, ...totals };
      if (!selected) {
        await apiFetch('/admin/invoices', { method: 'POST', headers, body: JSON.stringify(payload) });
      } else {
        await apiFetch(`/admin/invoices/${selected.id}`, { method: 'PUT', headers, body: JSON.stringify(payload) });
      }
      setModal(null); loadInvoices(); loadSummary();
    } catch { alert('Save failed'); } finally { setSaving(false); }
  };

  const handleMarkPaid = async (invId) => {
    await apiFetch(`/admin/invoices/${invId}`, {
      method: 'PUT', headers, body: JSON.stringify({ status: 'paid' }),
    });
    loadInvoices(); loadSummary();
  };

  const totals = invForm.line_items ? calcTotals(invForm.line_items, invForm.discount_pct, invForm.tax_pct) : {};

  const TXN_COLS = [
    { key: 'reference', label: 'Reference', render: v => <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--accent)' }}>{v}</span> },
    { key: 'payer_name', label: 'Payer', render: (v, row) => (
      <div><div style={{ fontWeight: 500 }}>{v || '—'}</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{row.payer_email}</div></div>
    )},
    { key: 'type', label: 'Type', render: v => <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{TYPE_LABELS[v] || v}</span> },
    { key: 'method', label: 'Method', render: v => <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{METHOD_LABELS[v] || v}</span> },
    { key: 'amount', label: 'Amount', render: (v, row) => (
      <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, color: 'var(--text-primary)' }}>
        {row.currency} {Number(v).toLocaleString()}
      </span>
    )},
    { key: 'status', label: 'Status', render: v => <span style={{ fontSize: 11, fontWeight: 600, color: STATUS_COLORS[v] || 'var(--text-muted)', textTransform: 'capitalize' }}>{v}</span> },
    { key: 'created_at', label: 'Date', render: v => <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{new Date(v).toLocaleDateString()}</span> },
  ];

  const INV_COLS = [
    { key: 'invoice_number', label: 'Invoice #', render: v => <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--accent)' }}>{v}</span> },
    { key: 'client_name', label: 'Client', render: (v, row) => (
      <div><div style={{ fontWeight: 500 }}>{v}</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{row.client_email}</div></div>
    )},
    { key: 'type', label: 'Type', render: v => <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{TYPE_LABELS[v] || v}</span> },
    { key: 'total', label: 'Total', render: (v, row) => (
      <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600 }}>{row.currency} {Number(v).toLocaleString()}</span>
    )},
    { key: 'status', label: 'Status', render: v => <span style={{ fontSize: 11, fontWeight: 600, color: STATUS_COLORS[v] || 'var(--text-muted)', textTransform: 'capitalize' }}>{v}</span> },
    { key: 'due_date', label: 'Due', render: v => v ? <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{new Date(v).toLocaleDateString()}</span> : '—' },
    { key: '_actions', label: '', render: (_, row) => (
      <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
        <Button variant="ghost" size="sm" onClick={() => openEditInvoice(row)}>Edit</Button>
        {row.status !== 'paid' && row.status !== 'cancelled' && (
          <Button variant="secondary" size="sm" onClick={() => handleMarkPaid(row.id)}>Mark Paid</Button>
        )}
      </div>
    )},
  ];

  const tabBtn = (t) => ({
    padding: '9px 18px', fontSize: 13, cursor: 'pointer', background: 'none', border: 'none',
    borderBottom: `2px solid ${tab === t ? 'var(--accent)' : 'transparent'}`,
    color: tab === t ? 'var(--accent)' : 'var(--text-muted)',
    fontWeight: tab === t ? 600 : 400, marginBottom: -1, transition: 'all 0.15s',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)' }}>
        <button style={tabBtn('overview')}     onClick={() => setTab('overview')}>Overview</button>
        <button style={tabBtn('transactions')} onClick={() => setTab('transactions')}>Transactions</button>
        <button style={tabBtn('invoices')}     onClick={() => setTab('invoices')}>Invoices</button>
      </div>

      {/* ── OVERVIEW ── */}
      {tab === 'overview' && summary && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Revenue stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            {[
              { label: 'Total Revenue',    value: fmt(summary.total_revenue),    color: 'var(--green-light)' },
              { label: 'Pending',          value: fmt(summary.total_pending),    color: 'var(--yellow)' },
              { label: 'Program Fees',     value: fmt(summary.program_revenue),  color: 'var(--blue-light)' },
              { label: 'Service Fees',     value: fmt(summary.service_revenue),  color: 'var(--accent)' },
            ].map(s => (
              <div key={s.label} style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '16px 20px' }}>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>{s.label}</div>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: s.color }}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Invoice stats */}
          {invSummary && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
              {[
                { label: 'Paid Invoices',       value: invSummary.paid_count,      sub: fmt(invSummary.total_paid),       color: 'var(--green-light)' },
                { label: 'Outstanding',          value: invSummary.sent_count,      sub: fmt(invSummary.total_outstanding), color: 'var(--yellow)' },
                { label: 'Overdue',              value: invSummary.overdue_count,   sub: fmt(invSummary.total_overdue),     color: 'var(--red-light)' },
                { label: 'Draft',                value: invSummary.draft_count,     sub: '—',                              color: 'var(--text-muted)' },
              ].map(s => (
                <div key={s.label} style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '16px 20px' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>{s.label}</div>
                  <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>{s.sub}</div>
                </div>
              ))}
            </div>
          )}

          {/* Payment method breakdown */}
          <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '20px 24px' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14, marginBottom: 16 }}>Revenue by Method</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              {[
                { label: 'MTN MoMo',  value: summary.momo_revenue,   color: '#f5a623' },
                { label: 'Stripe',    value: summary.stripe_revenue,  color: '#635bff' },
                { label: 'Donations', value: summary.donation_revenue, color: 'var(--green-light)' },
              ].map(m => {
                const total = Number(summary.total_revenue) || 1;
                const pct   = Math.round((Number(m.value) / total) * 100);
                return (
                  <div key={m.label} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{m.label}</span>
                        <span style={{ fontSize: 12, fontWeight: 600 }}>{pct}%</span>
                      </div>
                      <div style={{ height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, background: m.color, borderRadius: 3, transition: 'width 0.5s' }} />
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{fmt(m.value)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── TRANSACTIONS ── */}
      {tab === 'transactions' && (
        <>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Select value={filter.status} onChange={e => setFilter(f => ({ ...f, status: e.target.value }))} style={{ width: 150 }}>
              <option value="">All Statuses</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="refunded">Refunded</option>
            </Select>
            <Select value={filter.type} onChange={e => setFilter(f => ({ ...f, type: e.target.value }))} style={{ width: 180 }}>
              <option value="">All Types</option>
              {Object.entries(TYPE_LABELS).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
            </Select>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{pagination?.total ?? 0} transactions</span>
          </div>
          <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
            <Table columns={TXN_COLS} data={transactions} loading={loading} />
            <Pagination pagination={pagination} onPage={setPage} />
          </div>
        </>
      )}

      {/* ── INVOICES ── */}
      {tab === 'invoices' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 10 }}>
              <Select value={filter.status} onChange={e => setFilter(f => ({ ...f, status: e.target.value }))} style={{ width: 150 }}>
                <option value="">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
                <option value="cancelled">Cancelled</option>
              </Select>
              <span style={{ fontSize: 12, color: 'var(--text-muted)', alignSelf: 'center' }}>{pagination?.total ?? 0} invoices</span>
            </div>
            <Button onClick={openCreateInvoice}>+ New Invoice</Button>
          </div>
          <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
            <Table columns={INV_COLS} data={invoices} loading={loading} onRowClick={openEditInvoice} />
            <Pagination pagination={pagination} onPage={setPage} />
          </div>
        </>
      )}

      {/* ── INVOICE MODAL ── */}
      <Modal open={modal === 'invoice'} onClose={() => setModal(null)}
        title={selected ? `Invoice — ${selected.invoice_number}` : 'New Invoice'} width={720}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Input label="Client Name" value={invForm.client_name || ''} onChange={e => setInvForm(f => ({ ...f, client_name: e.target.value }))} />
            <Input label="Client Email" value={invForm.client_email || ''} onChange={e => setInvForm(f => ({ ...f, client_email: e.target.value }))} />
            <Input label="Client Phone" value={invForm.client_phone || ''} onChange={e => setInvForm(f => ({ ...f, client_phone: e.target.value }))} />
            <Select label="Invoice Type" value={invForm.type || 'service_fee'} onChange={e => setInvForm(f => ({ ...f, type: e.target.value }))}>
              {Object.entries(TYPE_LABELS).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
            </Select>
            <Input label="Due Date" type="date" value={invForm.due_date || ''} onChange={e => setInvForm(f => ({ ...f, due_date: e.target.value }))} />
            {selected && (
              <Select label="Status" value={invForm.status || 'draft'} onChange={e => setInvForm(f => ({ ...f, status: e.target.value }))}>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="paid">Paid</option>
                <option value="overdue">Overdue</option>
                <option value="cancelled">Cancelled</option>
              </Select>
            )}
            <div style={{ gridColumn: '1 / -1' }}>
              <Input label="Description" value={invForm.description || ''} onChange={e => setInvForm(f => ({ ...f, description: e.target.value }))} />
            </div>
          </div>

          {/* Line items */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <label style={labelStyle}>Line Items</label>
              <button onClick={() => setInvForm(f => ({ ...f, line_items: [...f.line_items, { name: '', qty: 1, unit_price: 0, total: 0 }] }))}
                style={{ fontSize: 12, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer' }}>+ Add Item</button>
            </div>
            <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Description', 'Qty', 'Unit Price', 'Total', ''].map(h => (
                      <th key={h} style={{ padding: '8px 10px', fontSize: 10, textAlign: 'left', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(invForm.line_items || []).map((item, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <td style={{ padding: '6px 10px' }}>
                        <input value={item.name} onChange={e => updateLineItem(idx, 'name', e.target.value)}
                          style={{ width: '100%', background: 'transparent', border: 'none', color: 'var(--text-primary)', fontSize: 12, outline: 'none' }} />
                      </td>
                      <td style={{ padding: '6px 10px', width: 60 }}>
                        <input type="number" value={item.qty} onChange={e => updateLineItem(idx, 'qty', parseFloat(e.target.value))}
                          style={{ width: '100%', background: 'transparent', border: 'none', color: 'var(--text-primary)', fontSize: 12, outline: 'none' }} />
                      </td>
                      <td style={{ padding: '6px 10px', width: 100 }}>
                        <input type="number" value={item.unit_price} onChange={e => updateLineItem(idx, 'unit_price', parseFloat(e.target.value))}
                          style={{ width: '100%', background: 'transparent', border: 'none', color: 'var(--text-primary)', fontSize: 12, outline: 'none' }} />
                      </td>
                      <td style={{ padding: '6px 10px', width: 100, fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>
                        {Number(item.total || 0).toLocaleString()}
                      </td>
                      <td style={{ padding: '6px 10px', width: 30 }}>
                        <button onClick={() => setInvForm(f => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }))}
                          style={{ color: 'var(--red-light)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12 }}>✕</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <div style={{ width: 280, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                <span style={{ color: 'var(--text-muted)' }}>Subtotal</span>
                <span>{fmt(totals.subtotal)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: 12 }}>
                <span style={{ color: 'var(--text-muted)' }}>Discount %</span>
                <input type="number" value={invForm.discount_pct || 0} onChange={e => setInvForm(f => ({ ...f, discount_pct: parseFloat(e.target.value) }))}
                  style={{ width: 60, background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text-primary)', padding: '3px 6px', fontSize: 12, textAlign: 'right' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, fontSize: 12 }}>
                <span style={{ color: 'var(--text-muted)' }}>Tax %</span>
                <input type="number" value={invForm.tax_pct || 0} onChange={e => setInvForm(f => ({ ...f, tax_pct: parseFloat(e.target.value) }))}
                  style={{ width: 60, background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text-primary)', padding: '3px 6px', fontSize: 12, textAlign: 'right' }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid var(--border)', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15 }}>
                <span>Total</span>
                <span style={{ color: 'var(--accent)' }}>{fmt(totals.total)}</span>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
            <Button onClick={handleSaveInvoice} loading={saving}>{selected ? 'Save Invoice' : 'Create Invoice'}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

const labelStyle = { fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.03em', display: 'block', marginBottom: 6 };